import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { axiosInstance } from '../App';
import { Trophy, RefreshCw, Phone, Users, Flame, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays, subDays, isToday } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useWtfLiveData } from '../hooks/useWtfLiveData';

// ─── TIER THRESHOLDS (easy to tune) ─────────────────────────────────────────

const TIER_GOLD = 90;
const TIER_SILVER = 85;
const TIER_BRONZE = 80;

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const formatINR = (amount) => {
  if (!amount && amount !== 0) return '--';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '--';
  return '\u20B9' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const formatShort = (v) => {
  if (v == null || isNaN(v)) return '--';
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return `\u20B9${v.toLocaleString('en-IN')}`;
};

const normalizeGymName = (name) => {
  if (!name) return '';
  return name.replace(/,/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
};

const safeNum = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
};

const REFRESH_INTERVAL = 300; // matches useWtfLiveData default (5 min) — WTF API calls are live, not cached

const getTier = (pct) => {
  if (pct >= TIER_GOLD) return 'gold';
  if (pct >= TIER_SILVER) return 'silver';
  if (pct >= TIER_BRONZE) return 'bronze';
  return 'red';
};

const TIER_STYLES = {
  gold: {
    header: 'bg-gradient-to-b from-yellow-600/30 to-yellow-900/10 border-yellow-500',
    badge: '\uD83E\uDD47',
    accent: 'text-yellow-400',
    barColor: 'bg-yellow-500',
  },
  silver: {
    header: 'bg-gradient-to-b from-gray-400/30 to-gray-700/10 border-gray-400',
    badge: '\uD83E\uDD48',
    accent: 'text-gray-300',
    barColor: 'bg-gray-400',
  },
  bronze: {
    header: 'bg-gradient-to-b from-orange-600/30 to-orange-900/10 border-orange-600',
    badge: '\uD83E\uDD49',
    accent: 'text-orange-400',
    barColor: 'bg-orange-500',
  },
  red: {
    header: 'bg-gradient-to-b from-red-900/20 to-slate-900 border-red-800/50',
    badge: '\uD83D\uDC80',
    accent: 'text-red-400',
    barColor: 'bg-red-500',
  },
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function Leaderboard() {
  const navigate = useNavigate();

  // ── Date Navigation ──
  const [selectedDate, setSelectedDate] = useState(new Date());
  const goToPrevDay = () => setSelectedDate(d => subDays(d, 1));
  const goToNextDay = () => {
    if (!isToday(selectedDate)) setSelectedDate(d => addDays(d, 1));
  };

  // ── WTF Live Feeds (always fetched for settlement/monthly data) ──
  const { data: settlementData, loading: settlLoading, error: settlError } = useWtfLiveData('settlement_stats', { leaderboard: true });
  const { data: fdData, loading: fdLoading } = useWtfLiveData('fd_performance', { leaderboard: true });
  const { data: liveSubscriptionStats, loading: subLoading } = useWtfLiveData('subscription_type_stats');
  const { data: dashboardReports, loading: dashLoading } = useWtfLiveData('dashboard_reports');
  const { data: leadStatsPerGym, loading: leadPerGymLoading } = useWtfLiveData('lead_stats_per_gym');

  // ── Date-aware subscription stats (for yesterday/historical) ──
  const [historicalSubStats, setHistoricalSubStats] = useState(null);
  const isViewingToday = isToday(selectedDate);

  useEffect(() => {
    if (isViewingToday) {
      setHistoricalSubStats(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const res = await axiosInstance.get(`/wtf/subscription_type_stats/by-date?date=${dateStr}`);
        if (!cancelled) setHistoricalSubStats(res.data);
      } catch (e) {
        console.warn('Failed to fetch historical subscription stats:', e);
        if (!cancelled) setHistoricalSubStats(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate, isViewingToday]);

  // Use historical data when viewing past dates, live data for today
  const subscriptionStats = isViewingToday ? liveSubscriptionStats : historicalSubStats;

  // ── Gym Hierarchy ──
  const [gymHierarchy, setGymHierarchy] = useState({});
  const [hierById, setHierById] = useState({});
  const [hierarchyLoaded, setHierarchyLoaded] = useState(false);

  const fetchHierarchy = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/fd-hierarchy/all');
      if (res.data?.data) setGymHierarchy(res.data.data);
      if (res.data?.by_gym_id) setHierById(res.data.by_gym_id);
    } catch (e) {
      console.warn('Failed to fetch gym hierarchy:', e);
    } finally {
      setHierarchyLoaded(true);
    }
  }, []);

  useEffect(() => { fetchHierarchy(); }, [fetchHierarchy]);

  // ── Daily Targets data ──
  const [dailyTargets, setDailyTargets] = useState(null);

  const fetchDailyTargets = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await axiosInstance.get(`/daily-targets/dashboard?date=${dateStr}&leaderboard=true`);
      setDailyTargets(res.data);
    } catch (e) {
      console.warn('Failed to fetch daily targets:', e);
      // Graceful degradation — don't crash, just show nothing extra
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchDailyTargets();
    const interval = setInterval(fetchDailyTargets, REFRESH_INTERVAL * 1000);
    return () => clearInterval(interval);
  }, [fetchDailyTargets]);

  // ── Derived: Daily Targets lookup maps ──
  const { dtByTl, dtByGym } = useMemo(() => {
    const tlMap = {};
    const gymMap = {};
    if (!dailyTargets?.clusters) return { dtByTl: tlMap, dtByGym: gymMap };
    dailyTargets.clusters.forEach((cluster) => {
      // Key by lowercase TL name (without " TL" suffix, since API has no suffix)
      const tlKey = (cluster.tl_name || '').trim().toLowerCase();
      if (tlKey) {
        tlMap[tlKey] = {
          target: cluster.cluster_target || 0,
          done: cluster.revenue_done || 0,
          pct: cluster.completion_pct || 0,
          gyms_reporting: (cluster.gyms || []).filter(g => g.revenue_done > 0).length,
          gym_count: cluster.gym_count || 0,
        };
      }
      // Index each gym by normalized name
      (cluster.gyms || []).forEach((gym) => {
        const gymKey = normalizeGymName(gym.gym_name);
        if (gymKey) {
          gymMap[gymKey] = {
            target: gym.target || 0,
            done: gym.revenue_done || 0,
            pct: gym.completion_pct || 0,
          };
        }
      });
    });
    return { dtByTl: tlMap, dtByGym: gymMap };
  }, [dailyTargets]);

  // ── Countdown timer ──
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((c) => (c <= 1 ? REFRESH_INTERVAL : c - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setCountdown(REFRESH_INTERVAL);
  }, [settlementData]);

  // ── Derived: settlements array ──
  const settlements = useMemo(() => {
    if (!settlementData) return [];
    return Array.isArray(settlementData) ? settlementData : [];
  }, [settlementData]);

  // ── Derived: FD array ──
  const fds = useMemo(() => {
    if (!fdData) return [];
    return Array.isArray(fdData) ? fdData : [];
  }, [fdData]);

  // ── Derived: FDs grouped by normalized gym name ──
  const fdsByGym = useMemo(() => {
    const lookup = {};
    fds.forEach((fd) => {
      const key = normalizeGymName(fd.gym_name);
      if (!key) return;
      if (!lookup[key]) lookup[key] = [];
      lookup[key].push({
        name: fd.fd_name || fd.name || fd.staff_name || 'Unknown',
        calls: safeNum(fd.total_calls || fd.calls || 0),
        today_achieved: safeNum(fd.today_achieved || fd.todays_achieved || 0),
      });
    });
    return lookup;
  }, [fds]);

  // ── Derived: Per-gym daily sales from subscription_type_stats ──
  const gymSalesToday = useMemo(() => {
    const lookup = {};
    const raw = subscriptionStats?.data || subscriptionStats;
    const stats = Array.isArray(raw) ? raw : [];
    stats.forEach((g) => {
      const key = normalizeGymName(g.gym_name);
      if (key) {
        lookup[key] = {
          total: safeNum(g.total),
          activeMembers: safeNum(g.active_members),
        };
      }
    });
    return lookup;
  }, [subscriptionStats]);

  // ── Derived: Per-gym followup stats ──
  const gymFollowups = useMemo(() => {
    const lookup = {};
    const stats = Array.isArray(leadStatsPerGym) ? leadStatsPerGym : [];
    stats.forEach((g) => {
      if (g.gym_id) {
        lookup[g.gym_id] = {
          todaysFollowups: safeNum(g.todays_follow_ups),
          followupsAdded: safeNum(g.followups_added_today),
          hotLeads: safeNum(g.hot_leads),
        };
      }
    });
    return lookup;
  }, [leadStatsPerGym]);

  // ── Derived: Dashboard reports ──
  const dashReports = useMemo(() => {
    if (!dashboardReports || Array.isArray(dashboardReports)) return {};
    return dashboardReports;
  }, [dashboardReports]);

  // ── Derived: Gym rows (enriched) ──
  const gymRows = useMemo(() => {
    return settlements.map((gym) => {
      const target = safeNum(gym.target_value);
      const achieved = safeNum(gym.target_acheived);
      const todayTarget = safeNum(gym.today_target || gym.todays_target);
      const normalizedName = normalizeGymName(gym.gym_name);
      const gymFds = fdsByGym[normalizedName] || [];
      const hier = hierById[gym.gym_id] || gymHierarchy[normalizedName];
      const tlName = hier?.tl_name || '--';
      const gymSales = gymSalesToday[normalizedName];
      const salesToday = gymSales ? gymSales.total : 0;
      const activeMembers = gymSales ? gymSales.activeMembers : 0;
      const gymFollow = gym.gym_id ? gymFollowups[gym.gym_id] : null;
      const achievementPct = target > 0 ? Math.round((achieved / target) * 100) : (achieved > 0 ? 100 : 0);

      // Daily Targets data for this gym
      const dtGym = dtByGym[normalizedName];

      return {
        gym_name: gym.gym_name || 'Unknown',
        gym_id: gym.gym_id || '',
        normalized_name: normalizedName,
        target,
        achieved,
        achievement_pct: achievementPct,
        today_target: todayTarget,
        fd_count: gymFds.length,
        fds: gymFds,
        sales_today: salesToday,
        active_members: activeMembers,
        followups_today: gymFollow ? gymFollow.followupsAdded : 0,
        hot_leads: gymFollow ? gymFollow.hotLeads : 0,
        tl_name: tlName,
        dt_today_target: dtGym ? dtGym.target : null,
        dt_today_done: dtGym ? dtGym.done : null,
        dt_today_pct: dtGym ? dtGym.pct : null,
      };
    });
  }, [settlements, fdsByGym, hierById, gymHierarchy, gymSalesToday, gymFollowups, dtByGym]);

  // ── Derived: TL aggregates with gym cards ──
  const prevOrderRef = useRef([]);
  const streakRef = useRef({});

  const tlColumns = useMemo(() => {
    const tlMap = {};
    gymRows.forEach((gym) => {
      const tl = gym.tl_name;
      if (!tl || tl === '--' || tl.toLowerCase() === 'unassigned') return;
      if (!tlMap[tl]) {
        tlMap[tl] = {
          tl_name: tl,
          gym_count: 0,
          fd_count: 0,
          monthly_target: 0,
          monthly_achieved: 0,
          today_sales: 0,
          today_target: 0,
          active_members: 0,
          followups_today: 0,
          hot_leads: 0,
          gyms: [],
        };
      }
      const entry = tlMap[tl];
      entry.gym_count += 1;
      entry.fd_count += gym.fd_count;
      entry.monthly_target += gym.target;
      entry.monthly_achieved += gym.achieved;
      entry.today_sales += gym.sales_today;
      entry.today_target += gym.today_target;
      entry.active_members += gym.active_members;
      entry.followups_today += gym.followups_today;
      entry.hot_leads += gym.hot_leads;
      entry.gyms.push(gym);
    });

    // Compute achievement % and sort by daily achievement %
    const sorted = Object.values(tlMap)
      .map((tl) => {
        const monthly_pct = tl.monthly_target > 0
          ? Math.round((tl.monthly_achieved / tl.monthly_target) * 100)
          : tl.monthly_achieved > 0 ? 100 : 0;
        const daily_pct = tl.today_target > 0
          ? Math.round((tl.today_sales / tl.today_target) * 100)
          : tl.today_sales > 0 ? 100 : 0;
        // Sort gyms within TL by achievement % descending
        const sortedGyms = [...tl.gyms].sort((a, b) => b.achievement_pct - a.achievement_pct);

        // Daily Targets data — match by first name (daily-targets has "Kunal", leaderboard has "Kunal Choudhary TL")
        const dtBaseName = tl.tl_name.replace(/\s+TL$/i, '').trim();
        const dtFirstName = dtBaseName.split(/\s+/)[0].toLowerCase();
        const dtData = dtByTl[dtBaseName.toLowerCase()] || dtByTl[dtFirstName] || null;

        return {
          ...tl,
          monthly_pct,
          daily_pct,
          gyms: sortedGyms,
          dt_target: dtData ? dtData.target : null,
          dt_done: dtData ? dtData.done : null,
          dt_pct: dtData ? dtData.pct : null,
          dt_gyms_reporting: dtData ? dtData.gyms_reporting : null,
          dt_gym_count: dtData ? dtData.gym_count : null,
        };
      })
      .sort((a, b) => b.daily_pct - a.daily_pct);

    // Track rank changes
    const prevOrder = prevOrderRef.current;
    const rankChanges = {};
    sorted.forEach((tl, newIdx) => {
      const oldIdx = prevOrder.indexOf(tl.tl_name);
      if (oldIdx === -1 || prevOrder.length === 0) {
        rankChanges[tl.tl_name] = 0; // new entry or first load
      } else {
        rankChanges[tl.tl_name] = oldIdx - newIdx; // positive = moved up
      }
    });

    // Track streaks (top 3 consecutive refreshes)
    const top3Names = sorted.slice(0, 3).map(t => t.tl_name);
    const streaks = streakRef.current;
    // Increment streak for top 3, reset for others
    Object.keys(streaks).forEach(name => {
      if (!top3Names.includes(name)) streaks[name] = 0;
    });
    top3Names.forEach(name => {
      streaks[name] = (streaks[name] || 0) + 1;
    });

    // Attach rank metadata
    const withMeta = sorted.map((tl, idx) => ({
      ...tl,
      rank: idx + 1,
      rankChange: rankChanges[tl.tl_name] || 0,
      streak: streaks[tl.tl_name] || 0,
    }));

    prevOrderRef.current = sorted.map(t => t.tl_name);
    return withMeta;
  }, [gymRows, dtByTl]);

  // ── Company totals ──
  const companyTotals = useMemo(() => {
    let target = 0, achieved = 0, tlSumSales = 0, todayTarget = 0, fdCount = 0;
    tlColumns.forEach((tl) => {
      target += tl.monthly_target;
      achieved += tl.monthly_achieved;
      tlSumSales += tl.today_sales;
      todayTarget += tl.today_target;
      fdCount += tl.fd_count;
    });
    const todaySales = safeNum(dashReports?.todaySale) || tlSumSales;
    const pct = target > 0 ? Math.round((achieved / target) * 100) : 0;
    return { target, achieved, todaySales, todayTarget, pct, fdCount };
  }, [tlColumns, dashReports]);

  const totalClosures = safeNum(dashReports.newsubs);

  // ── Loading state — wait for all live feeds (each is now a direct WTF API call, ~200-1000ms) ──
  const isLoading = !hierarchyLoaded || ((settlLoading || fdLoading || subLoading || dashLoading || leadPerGymLoading) && settlements.length === 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* ── Error Banner (stale data warning when WTF API fails) ── */}
      {settlError && (
        <div className="bg-red-950/60 border-b border-red-800/50 px-4 py-2 flex items-center gap-2 text-sm text-red-300">
          <RefreshCw className="w-3.5 h-3.5 shrink-0" />
          <span>Live data unavailable — showing last known values. ({settlError})</span>
        </div>
      )}
      {/* ── Header Bar ── */}
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800 px-4 py-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Trophy className="w-7 h-7 text-yellow-500" />
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                Leaderboard
                <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  LIVE
                </span>
              </h1>
              <p className="text-xs text-slate-400">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Date Navigation */}
            <div className="flex items-center gap-2 bg-slate-800 rounded-lg border border-slate-700 px-2 py-1">
              <button onClick={goToPrevDay} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-white font-medium min-w-[140px] text-center">
                {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d')}
                {!isToday(selectedDate) && <span className="text-slate-500 text-xs ml-1">({format(selectedDate, 'EEE')})</span>}
              </span>
              <button
                onClick={goToNextDay}
                disabled={isToday(selectedDate)}
                className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Company totals */}
            <div className="text-right hidden sm:block">
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">Company</div>
              <div className="text-sm font-bold text-white">
                <span className="text-green-400">{formatShort(companyTotals.achieved)}</span>
                <span className="text-slate-500 mx-1">/</span>
                <span>{formatShort(companyTotals.target)}</span>
                <span className={`text-xs ml-1.5 ${companyTotals.pct >= 80 ? 'text-green-400' : companyTotals.pct >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                  ({companyTotals.pct}%)
                </span>
              </div>
              <div className="text-[10px] text-slate-500">
                Today: <span className="text-emerald-400 font-semibold">{formatINR(companyTotals.todaySales)}</span>
                {safeNum(dashReports.newsubs) > 0 && (
                  <span className="ml-2 text-cyan-400">{safeNum(dashReports.newsubs)} today closures</span>
                )}
              </div>
            </div>

            {/* Countdown */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700">
              <RefreshCw className={`w-3.5 h-3.5 text-slate-400 ${countdown <= 5 ? 'animate-spin' : ''}`} />
              <span className="text-xs text-slate-400 tabular-nums">
                {countdown >= 60 ? `${Math.ceil(countdown / 60)}m` : `${countdown}s`}
              </span>
            </div>

            {/* Navigate to Sales Intelligence */}
            <button
              onClick={() => navigate('/sales-intelligence')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600/20 border border-amber-600/40 text-amber-400 text-xs font-semibold hover:bg-amber-600/30 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {'\uD83D\uDCCA'} Click here for Sales Intelligence
            </button>
          </div>
        </div>

        {/* Mobile Company Stats */}
        <div className="sm:hidden mt-2 flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2">
          <div>
            <div className="text-[10px] text-slate-500">Monthly</div>
            <div className="text-xs font-bold text-white">
              <span className="text-green-400">{formatShort(companyTotals.achieved)}</span>
              <span className="text-slate-500 mx-1">/</span>
              {formatShort(companyTotals.target)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-slate-500">Today</div>
            <div className="text-xs font-bold text-emerald-400">{formatINR(companyTotals.todaySales)}</div>
          </div>
        </div>
      </div>

      {/* ── Company Summary Cards ── */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 px-4 pt-3 pb-1">
        <CompactCard label="Total Sales" value={formatINR(companyTotals.todaySales)} color="emerald" />
        <CompactCard label="Monthly Target" value={formatShort(companyTotals.target)} color="slate" />
        <CompactCard label="Monthly Achieved" value={formatShort(companyTotals.achieved)} color="green" />
        <CompactCard label="Achievement" value={`${companyTotals.pct}%`} color={companyTotals.pct >= 80 ? 'green' : companyTotals.pct >= 50 ? 'yellow' : 'red'} />
        <CompactCard label="Total FDs" value={companyTotals.fdCount} color="blue" />
        <CompactCard label="Today Closures" value={totalClosures} color="cyan" />
      </div>

      {/* ── Kanban Board ── */}
      <div className="flex-1 overflow-x-auto overflow-y-auto p-4">
        {tlColumns.length > 0 ? (
          <div className="flex gap-4 min-h-full" style={{ minWidth: `${tlColumns.length * 296}px` }}>
            {tlColumns.map((tl, index) => (
              <TLColumn key={tl.tl_name} tl={tl} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-slate-500">
            No TL data available. Ensure settlement stats and gym hierarchy are loaded.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TL COLUMN ───────────────────────────────────────────────────────────────

function getStreakFire(streak) {
  if (streak >= 10) return '\uD83D\uDD25\uD83D\uDD25\uD83D\uDD25';
  if (streak >= 5) return '\uD83D\uDD25\uD83D\uDD25';
  if (streak >= 2) return '\uD83D\uDD25';
  return '';
}

function getMilestone(pct) {
  if (pct >= 100) return { text: 'TARGET HIT! \uD83C\uDFC6', color: 'text-yellow-400 bg-yellow-500/10' };
  if (pct >= 75) return { text: 'Almost there! \uD83D\uDE80', color: 'text-blue-400 bg-blue-500/10' };
  if (pct >= 50) return { text: 'Halfway! \u26A1', color: 'text-amber-400 bg-amber-500/10' };
  if (pct >= 25) return { text: 'Quarter way! \uD83C\uDFAF', color: 'text-slate-400 bg-slate-500/10' };
  return null;
}

function TLColumn({ tl, index }) {
  const navigate = useNavigate();
  const tier = getTier(tl.daily_pct);
  const style = TIER_STYLES[tier];
  const fire = getStreakFire(tl.streak || 0);
  const milestone = getMilestone(tl.daily_pct);

  return (
    <div
      className={`
        flex flex-col rounded-xl border-2 transition-all duration-700 ease-in-out
        ${style.header}
        w-[280px] min-w-[280px] flex-shrink-0
      `}
    >
      {/* Column Header */}
      <div className="px-4 py-3 border-b border-slate-700/50 relative">
        {/* Rank number watermark */}
        <span className="text-2xl font-black opacity-20 absolute top-2 left-3">#{index + 1}</span>

        <div className="flex items-center justify-between mb-1 pl-6">
          <h3 className="text-sm font-bold text-white truncate flex-1 flex items-center gap-1.5 cursor-pointer hover:text-emerald-400 transition-colors" onClick={() => navigate(`/leaderboard/${encodeURIComponent(tl.tl_id || tl.tl_name)}`)}>
            {index === 0 && <span className="text-2xl animate-bounce">{'\uD83D\uDC51'}</span>}
            {tl.tl_name}
            {fire && <span className="ml-1">{fire}</span>}
          </h3>
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            {/* Movement arrow */}
            {tl.rankChange > 0 && (
              <span className="text-xs font-bold text-green-400">{'\u2191'}{tl.rankChange}</span>
            )}
            {tl.rankChange < 0 && (
              <span className="text-xs font-bold text-red-400">{'\u2193'}{Math.abs(tl.rankChange)}</span>
            )}
            {tl.rankChange === 0 && index < (tl.rank || 999) && (
              <span className="text-xs text-slate-500">{'\u2014'}</span>
            )}
            <span className="text-lg flex-shrink-0">{style.badge}</span>
          </div>
        </div>

        {/* Today's sales hero number */}
        <div className="text-xl font-black text-emerald-400 tabular-nums leading-tight">
          {formatINR(tl.today_sales)}
          <span className="text-[10px] text-slate-500 font-normal ml-1">today</span>
        </div>

        {/* Daily Targets: done / target · pct% */}
        {tl.dt_target != null && tl.dt_target > 0 && (
          <div className="mt-1.5">
            <div className={`text-base font-black tabular-nums leading-tight ${
              tl.dt_pct >= 80 ? 'text-green-400' : tl.dt_pct >= 50 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {formatINR(tl.dt_done)} <span className="text-slate-500 font-normal">/</span> {formatINR(tl.dt_target)}
              <span className="ml-1.5 text-sm">{'\u00B7'} {Math.round(tl.dt_pct)}%</span>
            </div>
            {tl.dt_gyms_reporting != null && (
              <div className="text-[10px] text-slate-500 mt-0.5">
                Daily Target {'\u00B7'} {tl.dt_gyms_reporting}/{tl.dt_gym_count} gyms reporting
              </div>
            )}
          </div>
        )}

        {/* Monthly achievement */}
        <div className="text-xs text-slate-300 mt-1">
          {formatShort(tl.monthly_achieved)}
          <span className="text-slate-500 mx-1">/</span>
          {formatShort(tl.monthly_target)}
          <span className={`ml-1.5 font-semibold ${tl.monthly_pct >= 80 ? 'text-green-400' : tl.monthly_pct >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
            ({tl.monthly_pct}%)
          </span>
        </div>

        {/* Gym + FD counts */}
        <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-1.5">
          <span>{tl.gym_count} gyms</span>
          <span className="flex items-center gap-0.5"><Users className="w-3 h-3" /> {tl.fd_count} FDs</span>
          {tl.followups_today > 0 && (
            <span className="flex items-center gap-0.5 text-amber-400">
              <Phone className="w-3 h-3" /> {tl.followups_today}
            </span>
          )}
          {tl.hot_leads > 0 && (
            <span className="flex items-center gap-0.5 text-red-400">
              <Flame className="w-3 h-3" /> {tl.hot_leads}
            </span>
          )}
        </div>

        {/* Daily achievement progress bar */}
        <div className="mt-2">
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${style.barColor}`}
              style={{ width: `${Math.min(tl.daily_pct, 100)}%` }}
            />
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 text-right tabular-nums">
            {tl.daily_pct}% of daily target
          </div>
        </div>

        {/* Achievement milestone badge */}
        {milestone && (
          <div className={`mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full inline-block ${milestone.color}`}>
            {milestone.text}
          </div>
        )}
      </div>

      {/* Gym Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        {tl.gyms.map((gym) => (
          <GymCard key={gym.gym_id || gym.normalized_name} gym={gym} />
        ))}
      </div>
    </div>
  );
}

// ─── COMPACT CARD ─────────────────────────────────────────────────────────────

function CompactCard({ label, value, color = 'slate' }) {
  const colors = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    green: 'bg-green-500/10 border-green-500/30 text-green-400',
    yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
    red: 'bg-red-500/10 border-red-500/30 text-red-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
    slate: 'bg-slate-800 border-slate-700 text-slate-300',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${colors[color] || colors.slate}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

// ─── GYM CARD ────────────────────────────────────────────────────────────────

function GymCard({ gym }) {
  const pctColor = gym.achievement_pct >= 80
    ? 'text-green-400'
    : gym.achievement_pct >= 50
      ? 'text-yellow-400'
      : 'text-red-400';

  const barColor = gym.achievement_pct >= 80
    ? 'bg-green-500'
    : gym.achievement_pct >= 50
      ? 'bg-yellow-500'
      : 'bg-red-500';

  return (
    <div className="bg-slate-900/50 border border-slate-700/40 rounded-lg p-3 hover:border-slate-600/60 transition-all duration-500">
      {/* Gym name + today's sales */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h4 className="text-xs font-semibold text-white leading-tight break-words" title={gym.gym_name}>
          {gym.gym_name}
        </h4>
        <span className="text-sm font-bold text-emerald-400 tabular-nums flex-shrink-0">
          {gym.sales_today > 0 ? formatINR(gym.sales_today) : '--'}
        </span>
      </div>

      {/* Daily Target row */}
      {gym.dt_today_target != null && gym.dt_today_target > 0 && (
        <div className={`text-[10px] font-semibold tabular-nums mb-1 ${
          gym.dt_today_pct >= 80 ? 'text-green-400' : gym.dt_today_pct >= 50 ? 'text-yellow-400' : 'text-red-400'
        }`}>
          Today {formatINR(gym.dt_today_done)} / {formatINR(gym.dt_today_target)}
        </div>
      )}

      {/* Achievement bar */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${barColor}`}
            style={{ width: `${Math.min(gym.achievement_pct, 100)}%` }}
          />
        </div>
        <span className={`text-[10px] font-bold tabular-nums flex-shrink-0 ${pctColor}`}>
          {gym.achievement_pct}%
        </span>
      </div>

      {/* Monthly target / achieved */}
      <div className="text-[10px] text-slate-400 mb-1.5">
        {formatShort(gym.achieved)}
        <span className="text-slate-600 mx-0.5">/</span>
        {formatShort(gym.target)}
      </div>

      {/* FD names with revenue */}
      {gym.fds && gym.fds.length > 0 && (
        <div className="border-t border-slate-700/30 pt-1.5 mt-1">
          {gym.fds.map((fd, i) => (
            <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
              <span className="text-slate-400 truncate flex-1 mr-2">
                {fd.name}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {fd.today_achieved > 0 && (
                  <span className="text-emerald-400 font-semibold tabular-nums">
                    {formatINR(fd.today_achieved)}
                  </span>
                )}
                {fd.calls > 0 && (
                  <span className="text-slate-500 tabular-nums flex items-center gap-0.5">
                    <Phone className="w-2.5 h-2.5" />
                    {fd.calls}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
